import { Box, Button, Flex, Grid, Icon, Text, VStack } from '@chakra-ui/react';
import { FaDatabase, FaEnvelope, FaHeadset, FaArrowRight } from 'react-icons/fa';
import { headingFont } from 'theme/tokens';
import { translate } from 'i18n';
import MigrationTimeline from '../components/MigrationTimeline';
import WhatsAppCta from '../components/WhatsAppCta';

const SECTIONS = [
  {
    icon: FaDatabase,
    titleKey: 'marketing.migration_kit_section1_title',
    bodyKey: 'marketing.migration_kit_section1_body',
    color: 'brand.500',
    bg: 'brand.50',
  },
  {
    icon: FaEnvelope,
    titleKey: 'marketing.migration_kit_section2_title',
    bodyKey: 'marketing.migration_kit_section2_body',
    color: 'accent.500',
    bg: 'green.50',
  },
  {
    icon: FaHeadset,
    titleKey: 'marketing.migration_kit_section3_title',
    bodyKey: 'marketing.migration_kit_section3_body',
    color: 'purple.500',
    bg: 'purple.50',
  },
] as const;

export default function MigrationKitPage() {
  return (
    <Box>
      {/* Hero */}
      <Box
        bg="linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f0fdf4 100%)"
        px={{ base: 4, md: 8 }}
        pt={{ base: 12, md: 20 }}
        pb={{ base: 8, md: 14 }}
      >
        <VStack maxW="700px" mx="auto" spacing={5} textAlign="center">
          <Text
            fontFamily={headingFont}
            fontSize={{ base: '3xl', md: '5xl' }}
            fontWeight="800"
            lineHeight="1.1"
            letterSpacing="-0.03em"
            color="gray.900"
          >
            {translate('marketing.migration_kit_title')}
          </Text>
          <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600" maxW="540px">
            {translate('marketing.migration_kit_subtitle')}
          </Text>
        </VStack>
      </Box>

      {/* Timeline */}
      <Box px={{ base: 4, md: 8 }} py={{ base: 8, md: 14 }}>
        <Box maxW="800px" mx="auto">
          <MigrationTimeline />
        </Box>
      </Box>

      {/* Detail sections */}
      <Box bg="gray.50" px={{ base: 4, md: 8 }} py={14}>
        <Grid
          maxW="900px"
          mx="auto"
          templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
          gap={8}
        >
          {SECTIONS.map((section, i) => (
            <VStack
              key={i}
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="2xl"
              p={8}
              spacing={4}
              align="flex-start"
            >
              <Flex
                w="48px"
                h="48px"
                borderRadius="xl"
                bg={section.bg}
                align="center"
                justify="center"
              >
                <Icon as={section.icon} color={section.color} boxSize={5} />
              </Flex>
              <Text fontFamily={headingFont} fontWeight="700" fontSize="md" color="gray.800">
                {translate(section.titleKey as any)}
              </Text>
              <Text fontSize="sm" color="gray.600" lineHeight="1.7">
                {translate(section.bodyKey as any)}
              </Text>
            </VStack>
          ))}
        </Grid>
      </Box>

      {/* CTA */}
      <Box px={{ base: 4, md: 8 }} py={14} textAlign="center">
        <VStack spacing={5}>
          <Text fontFamily={headingFont} fontWeight="700" fontSize="xl" color="gray.800">
            {translate('marketing.migration_kit_cta')}
          </Text>
          <Flex
            direction={{ base: 'column', sm: 'row' }}
            gap={4}
            align="center"
          >
            <WhatsAppCta />
            <Button
              as="a"
              href="mailto:contact@mysportset.com"
              variant="outline"
              size="lg"
              borderRadius="full"
              borderColor="gray.300"
              color="gray.700"
              fontWeight="600"
              rightIcon={<Icon as={FaArrowRight} boxSize={3} />}
              _hover={{ borderColor: 'brand.500', color: 'brand.500' }}
            >
              contact@mysportset.com
            </Button>
          </Flex>
        </VStack>
      </Box>
    </Box>
  );
}
